
import { defineComponent, computed, ref, watch, PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import draggable from 'vuedraggable';
import Button from 'primevue/button';
import type { FlowStructureDto, FlowStepDto } from '@/types/flow-structure';
import { getOperatorById } from '@/components/flows/operators/registry';
import { parseOperatorParameters } from '@/helpers/flowParameters';

interface BranchItem {
  originalIndex: number; // 1-based index
  label: string;
  subtitle: string;
  icon: string;
  kind: string;
  isEmpty: boolean;
  childId?: string;
}

export default defineComponent({
  name: 'FlowBranchReorderModal',
  components: {
    draggable,
    Button,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    stepId: {
      type: String,
      default: null,
    },
    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const branches = ref<BranchItem[]>([]);

    const initBranches = () => {
      if (!props.flow || !props.stepId) return;

      const step = props.flow.steps.find((s) => s.id === props.stepId);
      if (!step) return;

      const operator = getOperatorById(step.type);
      if (!operator) return;

      // Determine total outputs
      let totalOutputs = 1;
      const outputsDef = operator.outputs ?? 1;
      if (typeof outputsDef === 'function') {
        const params = parseOperatorParameters(step.parameters);
        totalOutputs = outputsDef(params);
      } else {
        totalOutputs = outputsDef;
      }

      const items: BranchItem[] = [];

      for (let i = 1; i <= totalOutputs; i += 1) {
        // Find connected child
        const connection = props.flow.connections.find(
          (c) => c.parentId === props.stepId && c.parentOutput === i
        );

        if (connection) {
          const childStep = props.flow.steps.find((s) => s.id === connection.childId);
          if (childStep) {
            const childOperator = getOperatorById(childStep.type);

            // Replicate FlowNode label logic
            let labelText = childStep.type;
            if (childOperator) {
              if (childOperator.getDisplayLabel) {
                 const generated = childOperator.getDisplayLabel(parseOperatorParameters(childStep.parameters), t);
                 if (generated) labelText = generated;
                 else if (childOperator.label) labelText = t(childOperator.label);
              } else if (childOperator.label) {
                 labelText = t(childOperator.label);
              }
            }

            items.push({
              originalIndex: i,
              label: labelText,
              subtitle: childOperator?.getSubtitle ? (childOperator.getSubtitle(parseOperatorParameters(childStep.parameters), t) || '') : '',
              icon: childOperator?.icon ? `far ${childOperator.icon}` : 'far fa-square', // Default icon
              kind: childOperator?.kind || 'action',
              isEmpty: false,
              childId: childStep.id,
            });
          } else {
             // Connected but child not found (shouldn't happen)
             items.push(createEmptyBranch(i));
          }
        } else {
          items.push(createEmptyBranch(i));
        }
      }

      branches.value = items;
    };

    const createEmptyBranch = (index: number): BranchItem => ({
      originalIndex: index,
      label: t('flows.branchReorder.emptyOperator'),
      subtitle: '',
      icon: 'far fa-dashed-square',
      kind: 'empty',
      isEmpty: true,
    });

    watch(
      () => [props.visible, props.stepId, props.flow],
      () => {
        if (props.visible) {
          initBranches();
        }
      },
      { immediate: true }
    );

    const handleSave = () => {
      // Emit the new order of indices
      const newOrder = branches.value.map((b) => b.originalIndex);
      emit('save', { stepId: props.stepId, newOrder });
      emit('close');
    };

    return {
      t,
      branches,
      handleSave,
    };
  },
});
